/**
 * RFC 6238 TOTP (time-based one-time password) for MFA.
 *
 * Shared between client (QR provisioning URI) and server (verification). The
 * TOTP secret is an *authentication factor*, not vault data — the server must
 * hold it to verify codes at login (before the vault is unlocked). This is the
 * standard TOTP trade-off; see SECURITY-ARCHITECTURE.md §5.
 */
import { randomBytes, type Bytes } from './encoding.js';

const BASE32_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';

export function base32Encode(bytes: Bytes): string {
  let bits = 0;
  let value = 0;
  let output = '';
  for (let i = 0; i < bytes.length; i++) {
    value = (value << 8) | bytes[i]!;
    bits += 8;
    while (bits >= 5) {
      output += BASE32_ALPHABET[(value >>> (bits - 5)) & 31];
      bits -= 5;
    }
  }
  if (bits > 0) output += BASE32_ALPHABET[(value << (5 - bits)) & 31];
  return output;
}

export function base32Decode(b32: string): Bytes {
  const clean = b32.replace(/=+$/, '').toUpperCase().replace(/[^A-Z2-7]/g, '');
  let bits = 0;
  let value = 0;
  const output: number[] = [];
  for (const ch of clean) {
    const idx = BASE32_ALPHABET.indexOf(ch);
    if (idx < 0) continue;
    value = (value << 5) | idx;
    bits += 5;
    if (bits >= 8) {
      output.push((value >>> (bits - 8)) & 0xff);
      bits -= 8;
    }
  }
  return new Uint8Array(output);
}

/** Generate a random TOTP secret (20 bytes, base32) — typical 160-bit secret. */
export function generateTotpSecret(): { raw: Bytes; base32: string } {
  const raw = randomBytes(20);
  return { raw, base32: base32Encode(raw) };
}

/** otpauth:// provisioning URI for QR codes. */
export function totpUri(account: string, secretBase32: string, issuer = 'Battini Vault'): string {
  const label = encodeURIComponent(`${issuer}:${account}`);
  const params = new URLSearchParams({ secret: secretBase32, issuer, algorithm: 'SHA1', digits: '6', period: '30' });
  return `otpauth://totp/${label}?${params.toString()}`;
}

async function hmacSha1(key: Bytes, msg: Bytes): Promise<Bytes> {
  const cryptoKey = await crypto.subtle.importKey('raw', key, { name: 'HMAC', hash: 'SHA-1' }, false, ['sign']);
  const sig = await crypto.subtle.sign('HMAC', cryptoKey, msg);
  return new Uint8Array(sig);
}

function int32ToBytes(n: number): Bytes {
  // safe for n < 2^32
  return new Uint8Array([(n >>> 24) & 0xff, (n >>> 16) & 0xff, (n >>> 8) & 0xff, n & 0xff]);
}

/** Compute the 6-digit TOTP for a secret at a given unix time (default now). */
export async function totpCode(secretRaw: Bytes, unixTimeSeconds = Math.floor(Date.now() / 1000), step = 30): Promise<string> {
  const counter = Math.floor(unixTimeSeconds / step);
  const msg = new Uint8Array(8);
  const counterBytes = int32ToBytes(counter); // high 32 bits are 0 for any realistic time
  msg.set(counterBytes, 4);
  const hmac = await hmacSha1(secretRaw, msg);
  const offset = hmac[hmac.length - 1]! & 0x0f;
  const binary =
    ((hmac[offset]! & 0x7f) << 24) |
    ((hmac[offset + 1]! & 0xff) << 16) |
    ((hmac[offset + 2]! & 0xff) << 8) |
    (hmac[offset + 3]! & 0xff);
  const code = binary % 1_000_000;
  return code.toString().padStart(6, '0');
}

/**
 * Verify a TOTP with a small window (±1 step) to tolerate clock skew.
 * Constant-time comparison of the code string.
 */
export async function verifyTotp(
  secretRaw: Bytes,
  code: string,
  unixTimeSeconds = Math.floor(Date.now() / 1000),
  step = 30,
): Promise<boolean> {
  const clean = code.replace(/\s/g, '');
  if (!/^\d{6}$/.test(clean)) return false;
  for (const delta of [-step, 0, step]) {
    const expected = await totpCode(secretRaw, unixTimeSeconds + delta, step);
    if (constantTimeEqStr(expected, clean)) return true;
  }
  return false;
}

function constantTimeEqStr(a: string, b: string): boolean {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i++) diff |= a.charCodeAt(i) ^ b.charCodeAt(i);
  return diff === 0;
}
